// SPDX-License-Identifier: AGPL-3.0-or-later

import headerStyles from '@app/features/channel/components/ChannelHeader.module.css';
import {ChannelHeaderIcon} from '@app/features/channel/components/channel_header_components/ChannelHeaderIcon';
import styles from '@app/features/channel/components/channel_view/VoiceChannelChatPanel.module.css';
import type {Channel} from '@app/features/channel/models/Channel';
import {HIDE_CHAT_DESCRIPTOR} from '@app/features/voice/components/compact_voice_call_view/CompactCallHeightToggleButton';
import {useLingui} from '@lingui/react/macro';
import {ChatTeardropIcon, XIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useId} from 'react';

// A voice channel's own text chat beside its call. The width is fixed while the panel opens and closes (the slot around
// it clips it), so showing or hiding the chat never reflows the message list.
export const VoiceChannelChatPanel = observer(function VoiceChannelChatPanel({
	channel,
	onClose,
	children,
}: {
	channel: Channel;
	onClose: () => void;
	children: React.ReactNode;
}) {
	const {i18n} = useLingui();
	const titleId = useId();
	return (
		<section
			className={styles.panel}
			aria-labelledby={titleId}
			data-flx="channel.channel-view.voice-channel-chat-panel.panel"
		>
			<header className={styles.header} data-flx="channel.channel-view.voice-channel-chat-panel.header">
				<ChatTeardropIcon
					weight="fill"
					className={styles.headerIcon}
					aria-hidden="true"
					data-flx="channel.channel-view.voice-channel-chat-panel.header-icon"
				/>
				<h2 id={titleId} className={styles.headerTitle} data-flx="channel.channel-view.voice-channel-chat-panel.title">
					{channel.name ?? ''}
				</h2>
				<ChannelHeaderIcon
					icon={XIcon}
					iconWeight="bold"
					label={i18n._(HIDE_CHAT_DESCRIPTOR)}
					keybindAction="voice_toggle_compact_call_view"
					onClick={onClose}
					className={headerStyles.iconButtonDefault}
					data-flx="channel.channel-view.voice-channel-chat-panel.channel-header-icon.close"
				/>
			</header>
			<div className={styles.content} data-flx="channel.channel-view.voice-channel-chat-panel.content">
				{children}
			</div>
		</section>
	);
});
